import React, { memo, useMemo } from 'react';

import { StyleSheet, View } from 'react-native';

import { Typography } from 'components/ui';
import { primitive, spacing } from 'styles/constants';

type DiagnosticMarkdownBlock =
  | { type: 'title' | 'heading' | 'quote' | 'paragraph'; text: string }
  | { type: 'list-item'; text: string; depth: number; label?: string; value?: string };

const MARKDOWN_ESCAPE = /\\(.)/g;
const LIST_ITEM = /^(\s*)-\s+(.*)$/;
const BOLD_LABEL = /^\*\*(.+?):\*\*(?:\s(.*))?$/;
const PLAIN_LABEL = /^([A-Za-z][^:]{0,79}):(?:\s+(.*))?$/;

const unescapeMarkdown = (value: string) => value.replace(MARKDOWN_ESCAPE, '$1');

export const parseDiagnosticMarkdown = (markdown: string): DiagnosticMarkdownBlock[] =>
  markdown.split(/\r?\n/).reduce<DiagnosticMarkdownBlock[]>((blocks, line) => {
    if (!line.trim()) {
      return blocks;
    }
    if (line.startsWith('# ')) {
      blocks.push({ type: 'title', text: unescapeMarkdown(line.slice(2)) });
      return blocks;
    }
    if (line.startsWith('## ')) {
      blocks.push({ type: 'heading', text: unescapeMarkdown(line.slice(3)) });
      return blocks;
    }
    if (line.startsWith('> ')) {
      blocks.push({ type: 'quote', text: unescapeMarkdown(line.slice(2)) });
      return blocks;
    }

    const listMatch = line.match(LIST_ITEM);
    if (listMatch) {
      const [, indentation, content] = listMatch;
      const labelMatch = content.match(BOLD_LABEL) ?? content.match(PLAIN_LABEL);
      blocks.push({
        type: 'list-item',
        depth: Math.min(4, Math.floor(indentation.length / 2)),
        text: unescapeMarkdown(content),
        label: labelMatch ? unescapeMarkdown(labelMatch[1]) : undefined,
        value: labelMatch?.[2] ? unescapeMarkdown(labelMatch[2]) : undefined,
      });
      return blocks;
    }

    blocks.push({ type: 'paragraph', text: unescapeMarkdown(line) });
    return blocks;
  }, []);

export const DiagnosticMarkdownPreview = memo(({ markdown }: { markdown: string }) => {
  const blocks = useMemo(() => parseDiagnosticMarkdown(markdown), [markdown]);

  return (
    <View style={styles.container} testID="diagnostic-context-markdown">
      {blocks.map((block, index) => {
        const key = `${index}-${block.type}`;
        if (block.type === 'title') {
          return (
            <Typography key={key} selectable style={styles.title}>
              {block.text}
            </Typography>
          );
        }
        if (block.type === 'heading') {
          return (
            <Typography key={key} selectable style={styles.heading}>
              {block.text}
            </Typography>
          );
        }
        if (block.type === 'quote') {
          return (
            <View key={key} style={styles.quote}>
              <Typography selectable style={styles.quoteText}>
                {block.text}
              </Typography>
            </View>
          );
        }
        if (block.type === 'list-item') {
          return (
            <Typography
              key={key}
              selectable
              style={[styles.listItem, listIndentStyles[block.depth]]}
            >
              {'• '}
              {block.label ? (
                <>
                  <Typography bold style={styles.label}>
                    {block.label}:
                  </Typography>
                  {block.value ? ` ${block.value}` : ''}
                </>
              ) : (
                block.text
              )}
            </Typography>
          );
        }
        return (
          <Typography key={key} selectable style={styles.paragraph}>
            {block.text}
          </Typography>
        );
      })}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    gap: spacing.$2,
  },
  title: {
    color: primitive.$bbqBrown,
    fontSize: 20,
    fontWeight: '700',
  },
  heading: {
    color: primitive.$bbqBrown,
    fontSize: 17,
    fontWeight: '700',
    marginTop: spacing.$2,
  },
  quote: {
    borderLeftWidth: 3,
    borderLeftColor: primitive.$blackOpacity30,
    paddingLeft: spacing.$3,
  },
  quoteText: {
    color: primitive.$bbqBrown,
    fontStyle: 'italic',
  },
  listItem: {
    color: primitive.$bbqBrown,
    lineHeight: 20,
  },
  listLevel0: {
    marginLeft: 0,
  },
  listLevel1: {
    marginLeft: spacing.$3,
  },
  listLevel2: {
    marginLeft: spacing.$4,
  },
  listLevel3: {
    marginLeft: spacing.$4 + spacing.$3,
  },
  listLevel4: {
    marginLeft: spacing.$4 * 2,
  },
  label: {
    color: primitive.$bbqBrown,
  },
  paragraph: {
    color: primitive.$bbqBrown,
    lineHeight: 20,
  },
});

const listIndentStyles = [
  styles.listLevel0,
  styles.listLevel1,
  styles.listLevel2,
  styles.listLevel3,
  styles.listLevel4,
];
